import { useEffect, useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import useDebounceState from '@/hooks/useDebounceState';
import useContents from '@/modules/corporate/hooks/useContents';
import { OrderEnum } from '@/types/schema';

import { ContentMenuItem, getContentFilterOption } from '../components/LearningContent/utils';

const useLearningContent = ({
  contentIds,
  onChange,
}: {
  contentIds: string[];
  onChange: (ids: string[]) => void;
}) => {
  const [selectedContentsState, setSelectedContentsState] = useState<ContentMenuItem[]>();
  const [open, { toggle }] = useToggle(false);
  const { contents, isFetching } = useContents({
    ids: contentIds,
    pause: !contentIds.length || !!selectedContentsState,
  });

  useEffect(() => {
    if (contentIds.length && !selectedContentsState && !isFetching) {
      const selectedContentsMap = contents?.map((content) => getContentFilterOption(content));
      setSelectedContentsState(selectedContentsMap);
    }
  }, [contentIds.length, contents, isFetching, selectedContentsState]);

  const [search, setSearch, searchValue] = useDebounceState('');
  const { contents: allContents, fetchMore } = useContents({
    searchString: searchValue,
    sort: [{ field: 'updatedAt', order: OrderEnum.Desc }],
  });

  const filterGroups: FilterGroup[] = useMemo(() => {
    return allContents?.length
      ? [
          {
            id: 'contents',
            options: allContents.map((content) => getContentFilterOption(content)),
          },
        ]
      : [];
  }, [allContents]);

  const onContentChange = useEvent((filterOptions: FilterOption[]) => {
    setSelectedContentsState(filterOptions as ContentMenuItem[]);
    const selectedContentIds = filterOptions.map((content) => content.value);
    onChange(selectedContentIds);
  });

  const onContentDelete = useEvent((contentId: string) => {
    const updatedArray = contentIds.filter((id) => contentId !== id);
    const newSelectedContentsState = selectedContentsState?.filter(
      (content) => content.id !== contentId,
    );
    onChange(updatedArray);
    setSelectedContentsState(newSelectedContentsState);
  });

  return {
    filterGroups,
    selectedContentsState,
    toggle,
    open,
    isFetching,
    search,
    setSearch,
    onContentChange,
    fetchMore,
    onContentDelete,
  };
};

export default useLearningContent;
